import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { endpoints, setTokenProvider, setUnauthorizedHandler } from "./api.js";
import { getOidcAccessToken, oidcSignOut } from "./auth.js";
import { getStoredTenantId, setStoredTenantId } from "./lib/storage.js";

const SessionContext = createContext(null);

function pickTenant(memberships) {
  if (!memberships?.length) return null;
  const stored = getStoredTenantId();
  if (stored && memberships.some((m) => m.tenantId === stored)) return stored;
  return memberships[0].tenantId;
}

/**
 * status: "loading" | "anon" | "ready" | "error"
 * Holds auth config, the signed-in user (/me), the selected tenant and its record (/tenant).
 */
export function SessionProvider({ children }) {
  const [status, setStatus] = useState("loading");
  const [authConfig, setAuthConfig] = useState(null);
  const [me, setMe] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [error, setError] = useState(null);
  const statusRef = useRef(status);
  statusRef.current = status;

  const loadTenant = useCallback(async () => {
    try {
      const { tenant: t } = await endpoints.tenant();
      setTenant(t);
      return t;
    } catch {
      setTenant(null);
      return null;
    }
  }, []);

  const loadMe = useCallback(async () => {
    try {
      const data = await endpoints.me();
      const tid = pickTenant(data.memberships);
      setStoredTenantId(tid);
      setTenantId(tid);
      setMe(data);
      if (tid) await loadTenant();
      else setTenant(null);
      setStatus("ready");
      return data;
    } catch (e) {
      if (e.status === 401) {
        setMe(null);
        setTenant(null);
        setStatus("anon");
        return null;
      }
      setError(e);
      setStatus("error");
      return null;
    }
  }, [loadTenant]);

  const boot = useCallback(async () => {
    setStatus("loading");
    setError(null);
    try {
      const cfg = await endpoints.authConfig();
      setAuthConfig(cfg);
      if (cfg.mode === "oidc") setTokenProvider(() => getOidcAccessToken(cfg));
      await loadMe();
    } catch (e) {
      setError(e);
      setStatus("error");
    }
  }, [loadMe]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (statusRef.current === "ready") {
        setMe(null);
        setTenant(null);
        setStatus("anon");
      }
    });
    boot();
    return () => setUnauthorizedHandler(null);
  }, [boot]);

  const selectTenant = useCallback(
    async (id) => {
      setStoredTenantId(id);
      setTenantId(id);
      setTenant(null);
      await loadTenant();
    },
    [loadTenant],
  );

  const logout = useCallback(async () => {
    if (authConfig?.mode === "oidc") {
      await oidcSignOut(authConfig);
      return;
    }
    try {
      await endpoints.logout();
    } catch {
      /* cookie may already be gone */
    }
    setMe(null);
    setTenant(null);
    setStatus("anon");
  }, [authConfig]);

  const membership = me?.memberships?.find((m) => m.tenantId === tenantId) || null;

  const value = useMemo(
    () => ({
      status,
      error,
      authConfig,
      me,
      user: me?.user || null,
      memberships: me?.memberships || [],
      membership,
      role: membership?.role || null,
      tenantId,
      tenant,
      setTenant,
      boot,
      refreshMe: loadMe,
      refreshTenant: loadTenant,
      selectTenant,
      logout,
    }),
    [status, error, authConfig, me, membership, tenantId, tenant, boot, loadMe, loadTenant, selectTenant, logout],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession outside SessionProvider");
  return ctx;
}

export const canManage = (role) => role === "owner" || role === "admin";
export const canReview = (role) => role === "owner" || role === "admin" || role === "analyst";
